import React, {
  createContext,
  Dispatch,
  FunctionComponent,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { difference } from 'lodash';
import authClient from '../../api/authClient/authClient';
import { Devices } from '../../api/models';
import userClient from '../../api/userClient';
import { MaybeUser } from './models';
import { reconcileDevices } from './utils';
import { getLocalTestGroups, getLocalTestGroupsToDelete, clearAllLocalTestGroups } from 'packages/smart-home-support/utils';

export type UserContextProps = {
  user: MaybeUser;
  setUser: (user: MaybeUser) => void;
  setDevices: (devices: Devices) => void;
};

const notImplemented = () => {
  throw new Error('Missing UserProvider');
};

export const UserContext = createContext<UserContextProps>({
  user: null,
  setUser: notImplemented,
  setDevices: notImplemented,
});
UserContext.displayName = 'User';

export type UserProviderProps = {
  isAuthEnabled: boolean;
};

/* user will be undefined while loading, and either a User object or null (if not logged in) once loaded */
export const UserProvider: FunctionComponent<UserProviderProps> = ({ isAuthEnabled, children }) => {
  const [user, setUser] = useState<MaybeUser>(undefined);
  const localTestGroupsToAdd = getLocalTestGroups();
  const localTestGroupsToRemove = getLocalTestGroupsToDelete();

  const setUserCallback = useCallback((user: MaybeUser) => {
    if (user) {
      let combinedTestGroups: Array<string> = [];
  
      const devices = reconcileDevices(user.home.devices);
      const userTestGroups = user.testGroups || [];
      const userTestGroupsWithDeletions = difference(userTestGroups, localTestGroupsToRemove);
      
      if (devices !== user.home.devices) {
        userClient.saveDevices(devices);
      }

      // Only update the user record if there is something changed
      if (localTestGroupsToAdd.length > 0 || localTestGroupsToRemove.length > 0) {
        combinedTestGroups = userTestGroupsWithDeletions.concat(localTestGroupsToAdd);
        userClient.saveTestGroups(combinedTestGroups);
      } else {
        combinedTestGroups = userTestGroups;
      }

      const updatedUser = { ...user, testGroups: combinedTestGroups, home: { ...user.home, devices } };

      setUser(updatedUser);
      clearAllLocalTestGroups(updatedUser);
    } else {
      setUser(user);
    }
  }, []);

  const setDevicesCallback = useCallback(
    (devices: Devices) =>
      setUser(
        (user) =>
          user && {
            ...user,
            home: { ...user.home, devices },
          }
      ),
    []
  );

  useEffect(() => {
    if (!isAuthEnabled) {
      return;
    }

    let isSubscribed = true;

    authClient.getUser().then(
      (retrievedUser) => {
        if (isSubscribed) {
          setUserCallback(retrievedUser);
        }
      },
      () => {
        if (isSubscribed) {
          setUser(null);
        }
      }
    );

    return () => {
      isSubscribed = false;
    };
  }, [isAuthEnabled]);

  return (
    <UserContext.Provider value={{ user, setUser: setUserCallback, setDevices: setDevicesCallback }}>
      {children}
    </UserContext.Provider>
  );
};



// WEBPACK FOOTER //
// ./packages/smart-home-support/pages/auth/UserProvider.tsx